/**
 * ClientSwitcher Component
 *
 * Dropdown for coaches to switch between viewing their own data
 * and their clients' data in FitTrack.
 * Mobile-first responsive design with touch-friendly targets.
 */

import { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, User, Users, Check } from 'lucide-react';
import { useCoachStore } from '../../stores/coachStore';

export default function ClientSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    isActingAsCoach,
    clients,
    loadClients,
    viewingClientId,
    viewingClientInfo,
    setViewingClient,
    clearViewingClient,
  } = useCoachStore();

  // Load clients on mount if acting as coach
  useEffect(() => {
    if (isActingAsCoach && clients.length === 0) {
      loadClients();
    }
  }, [isActingAsCoach, clients.length, loadClients]);

  // Sync URL param with store on mount
  useEffect(() => {
    const clientIdFromUrl = searchParams.get('client');

    if (clientIdFromUrl && clientIdFromUrl !== viewingClientId) {
      // Find client info from clients list
      const client = clients.find((c) => c.client_id === clientIdFromUrl);
      if (client) {
        setViewingClient(clientIdFromUrl, {
          id: client.client_id,
          email: client.client?.email,
          display_name: client.client?.display_name || client.client?.email?.split('@')[0] || 'Öğrenci',
          avatar_url: client.client?.avatar_url,
        });
      }
    } else if (!clientIdFromUrl && viewingClientId) {
      clearViewingClient();
    }
  }, [searchParams, clients, viewingClientId, setViewingClient, clearViewingClient]);

  // Note: FitTrackPage handles initialize() when viewingClientId changes

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if not a coach
  if (!isActingAsCoach) {
    return null;
  }

  const handleSelectSelf = () => {
    clearViewingClient();
    setSearchParams({});
    setIsOpen(false);
  };

  const handleSelectClient = (client) => {
    setViewingClient(client.client_id, {
      id: client.client_id,
      email: client.client?.email,
      display_name: client.client?.display_name || client.client?.email?.split('@')[0] || 'Öğrenci',
      avatar_url: client.client?.avatar_url,
    });
    setSearchParams({ client: client.client_id });
    setIsOpen(false);
  };

  const getDisplayName = () => {
    if (viewingClientId && viewingClientInfo) {
      return viewingClientInfo.display_name || viewingClientInfo.email?.split('@')[0] || 'Öğrenci';
    }
    return 'Kendim';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
          viewingClientId
            ? 'bg-blue-900/30 text-blue-300 active:bg-blue-800/40'
            : 'bg-gray-800 text-gray-300 active:bg-gray-700'
        }`}
      >
        {viewingClientId ? (
          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        ) : (
          <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        )}
        <span className="max-w-[80px] sm:max-w-[120px] truncate">{getDisplayName()}</span>
        <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-1.5 w-52 sm:w-56 bg-gray-800 rounded-xl shadow-xl border border-gray-700 py-1.5 z-50 overflow-hidden"
          >
            {/* Self option */}
            <button
              onClick={handleSelectSelf}
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-3 text-sm text-left active:bg-gray-700 hover:bg-gray-700/70 transition-colors"
            >
              <User className="w-4 h-4 text-gray-400" />
              <span className="flex-1 text-gray-100">Kendim</span>
              {!viewingClientId && (
                <Check className="w-4 h-4 text-green-400" />
              )}
            </button>

            {/* Divider */}
            {clients.length > 0 && (
              <div className="border-t border-gray-700 my-1.5" />
            )}

            {/* Clients list header */}
            {clients.length > 0 && (
              <div className="px-3 sm:px-4 py-1.5">
                <span className="text-2xs sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Öğrencilerim
                </span>
              </div>
            )}

            {/* Clients list with max height and scroll */}
            <div className="max-h-48 overflow-y-auto">
              {clients.map((client) => {
                const clientName = client.client?.display_name || client.client?.email?.split('@')[0] || 'Öğrenci';
                const avatarUrl = client.client?.avatar_url;
                const isSelected = viewingClientId === client.client_id;

                return (
                  <button
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className="w-full flex items-center gap-3 px-3 sm:px-4 py-3 text-sm text-left active:bg-gray-700 hover:bg-gray-700/70 transition-colors"
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={clientName}
                        className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-blue-300">
                          {clientName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="flex-1 text-gray-100 truncate">
                      {clientName}
                    </span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            {clients.length === 0 && (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">
                Henüz öğrenciniz yok
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
